import { useEffect, useRef } from "react";
import {
	type ChatMessage,
	type DestructiveAction,
	type IntentContract,
	type PlanContract,
	type TestPlan,
	testingAuthHeaders,
	testingAxios,
} from "@/services/testing";
import { useActiveOrgId } from "@/lib/org-scope/react";
import { type RenderedMessage, useTestingStore } from "@/stores/testingStore";

/**
 * Rebuilds a conversation's chat thread from server-persisted messages on load.
 *
 * `byScope` (the chat threads) is in-memory only, so after a reload a conversation showed its grouping
 * (from `chat_session_id`) but an empty thread — selecting a plan fell back to the bare plan overview.
 * This fetches each conversation's messages and repopulates its scope, keyed by the SERVER session id
 * (which is also `plan.chat_session_id`), so the cards render and in-chat scroll works again. Purely
 * additive: `hydrateScope` never overwrites a scope already in memory, so a live thread always wins.
 */

/**
 * The metadata the backend persists on a plan-proposal message (see the testing-service chat router).
 * Declared here so the backend↔client hydration contract lives in one typed place rather than as
 * scattered casts on the raw `unknown` JSON.
 */
interface PersistedProposalMeta {
	kind?: string;
	plan_id?: string;
	plan_contract?: PlanContract | null;
	intent_contract?: IntentContract | null;
	intent_gaps?: string[] | null;
	destructive_actions?: DestructiveAction[] | null;
}

const chatMessagesEndpoint = (sessionId: string) =>
	`/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`;

async function fetchChatMessages(sessionId: string): Promise<ChatMessage[]> {
	const r = await testingAxios.get<{ messages: ChatMessage[] }>(chatMessagesEndpoint(sessionId), {
		headers: testingAuthHeaders(),
	});
	return r.data.messages ?? [];
}

/**
 * Maps persisted chat messages back to the client's RenderedMessage shape. A proposal was persisted as
 * an empty-content assistant message carrying `metadata.kind='plan_proposal'` — its plan card is
 * rebuilt from the live plan record (by id) plus the contracts captured in metadata. Pure, so the
 * mapping is unit-tested without a network. Orphaned proposals (plan since deleted) are dropped.
 */
export function chatMessagesToRendered(messages: ChatMessage[], plans: TestPlan[]): RenderedMessage[] {
	const plansById = new Map(plans.map((p) => [p.id, p]));
	const out: RenderedMessage[] = [];
	for (const m of messages) {
		const at = m.inserted_at ? Date.parse(m.inserted_at) : undefined;
		if (m.role === "user") {
			out.push({ id: m.id, role: "user", content: m.content, at });
			continue;
		}
		const meta = (m.metadata ?? {}) as PersistedProposalMeta;
		if (meta.kind === "plan_proposal") {
			const plan = meta.plan_id ? plansById.get(meta.plan_id) : undefined;
			if (!plan) continue; // plan deleted → drop the orphaned card
			out.push({
				id: m.id,
				role: "assistant",
				content: "",
				// The run-confirmation gate reads destructive_actions off the snapshot, so carry the
				// persisted list (the plan record itself doesn't store it).
				proposedPlan: meta.destructive_actions?.length
					? { ...plan, destructive_actions: meta.destructive_actions }
					: plan,
				planContract: meta.plan_contract ?? undefined,
				intentContract: meta.intent_contract ?? undefined,
				intentGaps: meta.intent_gaps ?? undefined,
				at,
			});
			continue;
		}
		out.push({ id: m.id, role: "assistant", content: m.content, at });
	}
	return out;
}

/** When a plan was last touched — matches sessions.ts's ordering so "earliest plan" agrees everywhere. */
function planTouchedAt(p: TestPlan): number {
	const ts = Date.parse(p.updated_at ?? p.created_at);
	return Number.isFinite(ts) ? ts : 0;
}

/** The scope key a conversation's thread lives under — the live promote key, `plan:<firstPlanId>`. */
function conversationScopeKey(earliestPlanId: string): string {
	return `plan:${earliestPlanId}`;
}

export function useChatThreadHydration(plans: TestPlan[], enabled: boolean): void {
	const hydrateScope = useTestingStore((s) => s.hydrateScope);
	const orgId = useActiveOrgId();
	// Sessions already fetched — so the effect re-running (plans refetch) doesn't refetch. Tagged with
	// the org it belongs to: switching org resets the store's byScope, so the cache must reset too or
	// returning to an org would leave its threads un-hydrated.
	const attempted = useRef<{ org: string | null; ids: Set<string> }>({ org: orgId, ids: new Set() });

	useEffect(() => {
		if (!enabled || plans.length === 0) return;
		if (attempted.current.org !== orgId) attempted.current = { org: orgId, ids: new Set() };
		const { ids } = attempted.current;

		// The earliest plan of each conversation — its id is the scope key the live thread uses, so a
		// hydrated thread lands on (and yields to) exactly that scope rather than a second, orphan one.
		const earliestBySession = new Map<string, TestPlan>();
		for (const p of plans) {
			if (!p.chat_session_id) continue;
			const cur = earliestBySession.get(p.chat_session_id);
			if (!cur || planTouchedAt(p) < planTouchedAt(cur)) earliestBySession.set(p.chat_session_id, p);
		}

		for (const [sessionId, earliest] of earliestBySession) {
			if (ids.has(sessionId)) continue;
			const scopeKey = conversationScopeKey(earliest.id);
			// A live (or already-hydrated) thread owns this scope — leave it alone, don't even refetch.
			if (useTestingStore.getState().byScope[scopeKey]) {
				ids.add(sessionId);
				continue;
			}
			ids.add(sessionId);
			void fetchChatMessages(sessionId)
				.then((msgs) => {
					const rendered = chatMessagesToRendered(msgs, plans);
					if (rendered.length === 0) return;
					const lastProposed =
						[...rendered].reverse().find((m) => m.proposedPlan)?.proposedPlan?.id ?? null;
					hydrateScope(scopeKey, sessionId, rendered, lastProposed);
				})
				// Best-effort: grouping still works from chat_session_id. Allow a retry on a transient fail.
				.catch(() => ids.delete(sessionId));
		}
	}, [plans, enabled, orgId, hydrateScope]);
}
